import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, CircularProgress, IconButton, Typography } from "@mui/material";
import ChevronLeft from "@mui/icons-material/ChevronLeft";
import ChevronRight from "@mui/icons-material/ChevronRight";
import CheckCircleRounded from "@mui/icons-material/CheckCircleRounded";
import PlaceOutlined from "@mui/icons-material/PlaceOutlined";
import servicesService from "../../api/servicesService";
import pestReportService from "../../api/pestReportService";
import { useAuth } from "../../context/authContext";
import { getBusinessDateParts, hourToBusinessMinutes } from "../../utils/dateUtils";
import {
  eventBelongsToCalendarDay,
  isGhostOccurrence,
  normalizeCalendarEvents,
  to12Hour,
} from "../Calendario/utils";
import logoBremar from "../../assets/logo-bremar.png";
import ReportPanel, { clearServiceDraft, patchReportDraft, readReportDraft } from "./ReportPanel";

// Cuántos días hacia atrás puede navegar el técnico.
const MAX_DAYS_BACK = 3;
// Hora (24h) a partir de la cual se habilita ver el día siguiente.
const NEXT_DAY_UNLOCK_HOUR = 16;

const SERVICE_TYPE_META = {
  MIP: { label: "MIP", color: "#1D4E89" },
  TICKET: { label: "Ticket", color: "#B45309" },
  NEBULIZACION: { label: "Nebulización", color: "#0F766E" },
  MICROINYECCION: { label: "Microinyección", color: "#6D28D9" },
  MOSCA: { label: "Mosca", color: "#3F6212" },
  ABEJA: { label: "Abeja", color: "#A16207" },
  EQUIPO: { label: "Equipo", color: "#334155" },
  CHOQUE: { label: "Choque", color: "#B91C1C" },
  SUPERVISION: { label: "Supervisión", color: "#0369A1" },
  EVENTUAL: { label: "Eventual", color: "#9A3412" },
  OTRO: { label: "Otro", color: "#57534E" },
  MAINTENANCE: { label: "Mantenimiento", color: "#1D4E89" },
};

const TYPE_PALETTE = [
  "#1D4E89",
  "#0F766E",
  "#B45309",
  "#6D28D9",
  "#9F1239",
  "#3F6212",
  "#0369A1",
  "#9A3412",
  "#334155",
];

function typeMeta(service) {
  const raw = String(service?.type || service?.eventType || "").trim();
  if (!raw) return { label: "Servicio", color: "#57534E" };
  const key = raw.toUpperCase().replace(/\s+/g, "_");
  if (SERVICE_TYPE_META[key]) return SERVICE_TYPE_META[key];
  const byLabel = Object.values(SERVICE_TYPE_META).find(
    (item) => item.label.toLowerCase() === raw.toLowerCase()
  );
  if (byLabel) return byLabel;
  let hash = 0;
  for (let i = 0; i < raw.length; i += 1) hash = raw.charCodeAt(i) + ((hash << 5) - hash);
  return { label: raw, color: TYPE_PALETTE[Math.abs(hash) % TYPE_PALETTE.length] };
}

function placeOf(service) {
  const isEvent = service?._isFromServices !== true && Boolean(service?.eventType);
  if (isEvent) return service?.title || typeMeta(service).label;
  const client = service?.client?.name || "";
  const branch = service?.branch?.name || "";
  return [client, branch].filter(Boolean).join(" · ") || "Sin sucursal";
}

function startOfDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function dayDiff(a, b) {
  return Math.round((startOfDay(a) - startOfDay(b)) / (1000 * 60 * 60 * 24));
}

export default function AgendaTecnico() {
  const { effectiveUser } = useAuth();
  const techId = effectiveUser?.technician ? String(effectiveUser.technician) : "";
  const today = getBusinessDateParts();
  const [cursor, setCursor] = useState(() => {
    const base = new Date(today.year, today.month, today.day);
    const minMs = startOfDay(new Date(base.getFullYear(), base.getMonth(), base.getDate() - MAX_DAYS_BACK));
    const maxMs = startOfDay(new Date(base.getFullYear(), base.getMonth(), base.getDate() + (new Date().getHours() >= NEXT_DAY_UNLOCK_HOUR ? 1 : 0)));
    const draft = readReportDraft();
    if (draft?.cursor) {
      const saved = new Date(draft.cursor);
      const savedDay = startOfDay(saved);
      if (!Number.isNaN(saved.getTime()) && savedDay >= minMs && savedDay <= maxMs) return saved;
    }
    return base;
  });
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reported, setReported] = useState(() => new Set());
  const [reportService, setReportService] = useState(null);
  const restoredDraft = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await servicesService.getCalendar(cursor.getMonth() + 1, cursor.getFullYear());
      setEvents(normalizeCalendarEvents(res.data.data || []));
    } catch {
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [cursor]);

  useEffect(() => {
    load();
  }, [load]);

  const mine = useMemo(() => {
    if (!techId) return [];
    return events.filter((event) => String(event.technician?._id || event.technician || "") === techId);
  }, [events, techId]);

  const day = cursor.getDate();
  const dayEvents = mine
    .filter((event) => eventBelongsToCalendarDay(event, day))
    .sort((a, b) => hourToBusinessMinutes(a.hour) - hourToBusinessMinutes(b.hour));

  const reportKey = dayEvents
    .filter((event) => event._isFromServices === true && !isGhostOccurrence(event))
    .map((event) => String(event.originalServiceId || event._id || ""))
    .filter(Boolean)
    .join("|");

  useEffect(() => {
    const ids = reportKey ? reportKey.split("|") : [];
    if (!ids.length) {
      setReported(new Set());
      return;
    }
    let cancel = false;
    Promise.all(ids.map((id) => pestReportService.getReportsByEventId(id).then((rows) => [id, rows.length > 0]).catch(() => [id, false])))
      .then((rows) => {
        if (cancel) return;
        setReported(new Set(rows.filter(([, has]) => has).map(([id]) => id)));
      });
    return () => {
      cancel = true;
    };
  }, [reportKey]);

  useEffect(() => {
    if (loading || restoredDraft.current) return;
    restoredDraft.current = true;
    const draft = readReportDraft();
    if (!draft?.serviceId) return;
    const found = mine.find((event) => String(event.originalServiceId || event._id || "") === draft.serviceId);
    if (found) setReportService(found);
  }, [loading, mine]);

  const openReport = (service) => {
    patchReportDraft({
      serviceId: String(service.originalServiceId || service._id || ""),
      cursor: cursor.toISOString(),
    });
    setReportService(service);
  };

  const closeReport = () => {
    patchReportDraft({ serviceId: "" });
    setReportService(null);
  };

  const dateLabel = cursor.toLocaleDateString("es-MX", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });

  // Límites de navegación: hasta 3 días atrás y el día siguiente solo después de las 4 pm.
  const todayDate = useMemo(() => new Date(today.year, today.month, today.day), [today.year, today.month, today.day]);
  const nextDayUnlocked = new Date().getHours() >= NEXT_DAY_UNLOCK_HOUR;
  const offset = dayDiff(cursor, todayDate);
  const canGoBack = offset > -MAX_DAYS_BACK;
  const canGoForward = offset < (nextDayUnlocked ? 1 : 0);

  const shiftDay = (delta) => {
    if (delta < 0 && !canGoBack) return;
    if (delta > 0 && !canGoForward) return;
    const next = new Date(cursor);
    next.setDate(cursor.getDate() + delta);
    setCursor(next);
  };

  const relativeLabel =
    offset === 0 ? "Hoy" : offset === -1 ? "Ayer" : offset === 1 ? "Mañana" : null;

  const navBtnSx = (enabled) => ({
    bgcolor: "#fff",
    border: "1px solid #E5E7EB",
    borderRadius: "12px",
    width: 40,
    height: 40,
    color: enabled ? "#374151" : "#D1D5DB",
    boxShadow: enabled ? "0 1px 2px rgba(15,23,42,0.06)" : "none",
    "&:hover": { bgcolor: "#F9FAFB" },
  });

  return (
    <Box sx={{ maxWidth: 480, mx: "auto", width: "100%", pb: 5, px: 1.5 }}>
      {/* Encabezado tipo procedia */}
      <Box
        sx={{
          mt: 1.5,
          mb: 2,
          p: 2,
          bgcolor: "#fff",
          borderRadius: "18px",
          border: "1px solid #EEF0F3",
          boxShadow: "0 1px 2px rgba(15,23,42,0.05), 0 10px 24px rgba(15,23,42,0.06)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
          <Box component="img" src={logoBremar} alt="Bremar" sx={{ width: 30, height: 28, objectFit: "contain" }} />
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#9CA3AF", letterSpacing: "0.08em" }}>
            MI AGENDA
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton onClick={() => shiftDay(-1)} disabled={!canGoBack} aria-label="Día anterior" sx={navBtnSx(canGoBack)}>
            <ChevronLeft />
          </IconButton>
          <Box sx={{ flex: 1, textAlign: "center", minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "center", gap: 0.8 }}>
              {relativeLabel && (
                <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#111", letterSpacing: "-0.02em" }}>
                  {relativeLabel}
                </Typography>
              )}
              <Typography sx={{ fontSize: 12.5, color: "#6B7280", textTransform: "capitalize" }}>
                {dateLabel}
              </Typography>
            </Box>
            <Typography sx={{ fontSize: 12, color: "#9CA3AF", mt: 0.1 }}>
              {dayEvents.length} {dayEvents.length === 1 ? "servicio" : "servicios"}
            </Typography>
          </Box>
          <IconButton onClick={() => shiftDay(1)} disabled={!canGoForward} aria-label="Día siguiente" sx={navBtnSx(canGoForward)}>
            <ChevronRight />
          </IconButton>
        </Box>
      </Box>

      {!techId && (
        <Typography color="text.secondary">
          Esta cuenta de técnico no está ligada a un técnico. Pide al administrador que la relacione.
        </Typography>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
          {dayEvents.map((service) => {
            const meta = typeMeta(service);
            const address = service?.branch?.address || service?.location || "";
            const notes = service?.notes || "";
            const serviceId = String(service.originalServiceId || service._id || "");
            const canReport = service._isFromServices === true && Boolean(serviceId) && !isGhostOccurrence(service);
            const hasReport = reported.has(serviceId) || String(service.status || "").toUpperCase() === "COMPLETED";
            return (
              <Box
                key={`${service.originalServiceId}-${service.hour}`}
                sx={{
                  position: "relative",
                  bgcolor: "#fff",
                  borderRadius: "16px",
                  border: "1px solid #EEF0F3",
                  boxShadow: "0 1px 2px rgba(15,23,42,0.05)",
                  overflow: "hidden",
                  pl: 2,
                  pr: 1.75,
                  py: 1.6,
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    left: 0,
                    top: 10,
                    bottom: 10,
                    width: 4,
                    borderRadius: 99,
                    bgcolor: meta.color,
                  },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.4 }}>
                  <Typography sx={{ fontSize: 11, fontWeight: 800, color: meta.color, letterSpacing: "0.03em", textTransform: "uppercase" }}>
                    {meta.label}
                  </Typography>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#111", fontVariantNumeric: "tabular-nums" }}>
                    {service.hour ? to12Hour(service.hour) : "—"}
                  </Typography>
                </Box>
                <Typography sx={{ fontWeight: 650, fontSize: 15, color: "#111", lineHeight: 1.3 }}>
                  {placeOf(service)}
                </Typography>
                {address ? (
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5, mt: 0.5 }}>
                    <PlaceOutlined sx={{ fontSize: 15, color: "#9CA3AF", mt: 0.1, flexShrink: 0 }} />
                    <Typography sx={{ fontSize: 13, color: "#6B7280", lineHeight: 1.4 }}>{address}</Typography>
                  </Box>
                ) : null}
                {notes ? (
                  <Typography sx={{ fontSize: 12.5, color: "#6B7280", mt: 0.5, fontStyle: "italic", whiteSpace: "pre-wrap" }}>
                    {notes}
                  </Typography>
                ) : null}
                {canReport && (
                  hasReport ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, mt: 1.25, color: "#15803D" }}>
                      <CheckCircleRounded sx={{ fontSize: 18 }} />
                      <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Reporte enviado</Typography>
                    </Box>
                  ) : (
                    <Button
                      variant="contained"
                      disableElevation
                      onClick={() => openReport(service)}
                      sx={{
                        mt: 1.25,
                        minHeight: 42,
                        width: "100%",
                        borderRadius: "11px",
                        textTransform: "none",
                        fontWeight: 700,
                        fontSize: 14,
                        bgcolor: meta.color,
                        "&:hover": { bgcolor: meta.color, filter: "brightness(0.93)" },
                      }}
                    >
                      Subir reporte
                    </Button>
                  )
                )}
              </Box>
            );
          })}
        </Box>
      )}

      {techId && !loading && dayEvents.length === 0 && (
        <Box sx={{ textAlign: "center", py: 6 }}>
          <Typography sx={{ color: "#9CA3AF", fontSize: 14 }}>No tienes servicios este día.</Typography>
        </Box>
      )}

      {reportService && String(reportService.status || "").toUpperCase() !== "COMPLETED" && !reported.has(String(reportService.originalServiceId || reportService._id || "")) && (
        <ReportPanel
          service={reportService}
          place={placeOf(reportService)}
          alreadyReported={reported.has(String(reportService.originalServiceId || reportService._id || ""))}
          onClose={closeReport}
          onSaved={() => {
            const id = String(reportService.originalServiceId || reportService._id || "");
            setReported((prev) => new Set(prev).add(id));
            clearServiceDraft(id).catch(() => {});
            setReportService(null);
          }}
        />
      )}
    </Box>
  );
}
